import React from 'react';
import { secureLog } from '../lib/secureLogger';
import RichTextDescription from './RichTextDescription';
import { formatInvoiceVehicleDisplay, hasInvoiceVehicle } from '../lib/invoiceVehicleDisplay';

// Stamp components
const PaidStamp = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-30deg] border-[3px] border-green-600 rounded-lg px-6 sm:px-8 py-3 sm:py-4 opacity-30">
    <span className="text-xl sm:text-3xl font-bold text-green-600">PAID</span>
  </div>
);

const VoidedStamp = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-30deg] border-[3px] border-red-600 rounded-lg px-6 sm:px-8 py-3 sm:py-4 opacity-30">
    <span className="text-xl sm:text-3xl font-bold text-red-600">VOIDED</span>
  </div>
);

const PendingStamp = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-30deg] border-[3px] border-yellow-600 rounded-lg px-6 sm:px-8 py-3 sm:py-4 opacity-30">
    <span className="text-xl sm:text-3xl font-bold text-yellow-600">UNPAID</span>
  </div>
);

// Types
interface ServiceAddon {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes?: number;
}

interface BookingService {
  id: string;
  name: string;
  description: string | null;
  price: number;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year?: number | string | null;
  vehicle_color?: string | null;
  vehicle_vin?: string | null;
  vehicle_license_plate?: string | null;
  vehicle_name?: string;
  tax_exempt?: boolean;
  tax_rate?: number;
  add_ons?: ServiceAddon[];
}

interface BusinessInfo {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  gst_number?: string;
  pst_number?: string;
}

interface PaymentInfo {
  id: string;
  booking_id: string;
  business_id: string;
  amount: number;
  status: 'pending' | 'completed' | 'voided';
  created_at: string;
  notes?: string;
  booking_number: number;
  booking_title?: string;
  booking_subtotal?: number;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  booking_services?: BookingService[];
  booking_discounts?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
}

interface InvoiceDisplayProps {
  payment: PaymentInfo;
  business: BusinessInfo;
  className?: string;
  forwardRef?: React.RefObject<HTMLDivElement>;
}

export default function InvoiceDisplay({ payment, business, className = "", forwardRef }: InvoiceDisplayProps) {
  const isPending = payment.status === 'pending' || payment.id === 'pending';

  // Debug logging for InvoiceDisplay
  secureLog.log('🎨 InvoiceDisplay Component Debugging:');
  secureLog.log('- Received payment object:', payment);
  secureLog.log('- Payment ID:', payment.id);
  secureLog.log('- Booking ID:', payment.booking_id);
  secureLog.log('- Booking number:', payment.booking_number);
  secureLog.log('- Booking title:', payment.booking_title);
  secureLog.log('- booking_services exists?', !!payment.booking_services);
  secureLog.log('- booking_services length:', payment.booking_services?.length || 0);
  secureLog.log('- booking_services data:', payment.booking_services);
  secureLog.log('- Payment status:', payment.status);
  secureLog.log('- Amount:', payment.amount);
  secureLog.log('- Customer:', `${payment.customer_first_name} ${payment.customer_last_name}`);
  
  if (!payment.booking_services || payment.booking_services.length === 0) {
    secureLog.log('⚠️ No booking services found - will use fallback display');
    secureLog.log('- Will show booking title as service name:', payment.booking_title);
  } else {
    secureLog.log('✅ Booking services found - will show detailed breakdown');
    payment.booking_services.forEach((service, index) => {
      secureLog.log(`- Service ${index + 1}:`, {
        id: service.id,
        name: service.name,
        description: service.description,
        price: service.price,
        vehicle: formatInvoiceVehicleDisplay(service) || service.vehicle_name,
        addons: service.add_ons?.length || 0
      });
    });
  }

  return (
    <div ref={forwardRef} className={`bg-white shadow-lg rounded-lg overflow-hidden ${className}`}>
      {/* Invoice Header */}
      <div className="relative p-3 sm:p-8 border-b border-gray-200">
        {payment.status === 'completed' && <PaidStamp />}
        {payment.status === 'voided' && <VoidedStamp />}
        {isPending && <PendingStamp />}

        {/* Add void reason if payment is voided */}
        {payment.status === 'voided' && payment.notes && (
          <div className="mb-4 p-2 bg-red-50 border border-red-100 rounded-md">
            <p className="text-xs sm:text-sm text-red-600">
              <span className="font-medium">Void Reason:</span> {payment.notes}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
              {isPending ? 'INVOICE' : 'RECEIPT'}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">#{payment.booking_number}</p>
          </div>
          <div className="mt-2 sm:mt-0 sm:text-right">
            <p className="text-xs sm:text-sm text-gray-600">
              {isPending ? 'Invoice Date' : 'Payment Date'}
            </p>
            <p className="text-xs sm:text-sm font-medium">
              {new Date(
                isPending 
                  ? (payment as any).booking_created_at || payment.created_at
                  : (payment as any).payment_created_at || payment.created_at
              ).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Business & Customer Info */}
        <div className="flex flex-col sm:flex-row sm:justify-between mt-4 sm:mt-8">
          <div className="mb-3 sm:mb-0">
            <p className="text-xs sm:text-sm font-medium text-gray-900">From</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{business.name}</p>
            <p className="text-xs sm:text-sm text-gray-600">{business.address}</p>
            <p className="text-xs sm:text-sm text-gray-600">{business.phone}</p>
            <p className="text-xs sm:text-sm text-gray-600">{business.email}</p>
            {business.gst_number && (
              <p className="text-xs sm:text-sm text-gray-600 mt-1">GST: {business.gst_number}</p>
            )}
            {business.pst_number && (
              <p className="text-xs sm:text-sm text-gray-600">PST: {business.pst_number}</p>
            )}
          </div>
          <div className="sm:text-right">
            <p className="text-xs sm:text-sm font-medium text-gray-900">To</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              {payment.customer_first_name} {payment.customer_last_name}
            </p>
            <p className="text-xs sm:text-sm text-gray-600">{payment.customer_email}</p>
          </div>
        </div>
      </div>

      {/* Services Table */}
      <div className="p-3 sm:p-8">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[300px]">
            <thead>
              <tr className="text-xs sm:text-sm text-gray-600">
                <th className="text-left font-medium pb-2 w-[60%]">SERVICE</th>
                <th className="text-center font-medium pb-2 w-[20%]">VEHICLE</th>
                <th className="text-right font-medium pb-2 w-[20%]">AMOUNT</th>
              </tr>
            </thead>
            <tbody className="text-xs sm:text-sm divide-y divide-gray-200">
              {payment.booking_services?.map((service, index) => (
                <React.Fragment key={service.id}>
                  {/* Main Service */}
                  <tr>
                    <td className="py-3">
                      <div className="font-medium text-gray-900">{service.name}</div>
                    </td>
                    <td className="text-center text-gray-600 py-3">
                      {hasInvoiceVehicle(service)
                        ? formatInvoiceVehicleDisplay(service)
                        : service.vehicle_name || 'N/A'}
                    </td>
                    <td className="text-right text-gray-900 py-3">
                      ${service.price.toLocaleString()}
                    </td>
                  </tr>
                  {service.description && (
                    <tr>
                      <td colSpan={3} className="pb-3">
                        <RichTextDescription 
                          description={service.description} 
                          className="text-xs sm:text-sm text-gray-500 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-600" 
                        />
                      </td>
                    </tr>
                  )}
                  {/* Service Add-ons */}
                  {service.add_ons?.map((addon) => (
                    <tr key={addon.id} className="bg-gray-50">
                      <td className="py-2 pl-4">
                        <div className="text-gray-600">+ {addon.name}</div>
                        {addon.description && (
                          <div className="mt-0.5">
                            <RichTextDescription 
                              description={addon.description} 
                              className="text-xs text-gray-500 prose-headings:text-gray-600 prose-strong:text-gray-600 prose-em:text-gray-500" 
                            />
                          </div>
                        )}
                      </td>
                      <td className="text-center text-gray-500 py-2">-</td>
                      <td className="text-right text-gray-600 py-2">
                        ${addon.price.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )) || (
                // Fallback for payments without detailed service breakdown
                <tr>
                  <td className="py-3">
                    <div className="font-medium text-gray-900">{payment.booking_title || 'Service'}</div>
                  </td>
                  <td className="text-center text-gray-600 py-3">-</td>
                  <td className="text-right text-gray-900 py-3">
                    ${payment.amount.toLocaleString()}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="p-3 sm:p-8 bg-gray-50 border-t border-gray-200">
        <div className="w-full sm:w-80 sm:ml-auto">
          <div className="space-y-1 sm:space-y-2">
            {payment.booking_services?.length ? (
              // Detailed breakdown when service data is available
              <>
                <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                  <span>Services Subtotal</span>
                  <span>${(payment.booking_subtotal || payment.amount / 1.05).toLocaleString()}</span>
                </div>
                {payment.booking_discounts?.map((discount) => (
                  <div key={discount.id} className="flex justify-between text-xs sm:text-sm text-red-600">
                    <span>{discount.title}</span>
                    <span>
                      -${(discount.type === 'percentage' 
                        ? ((payment.booking_subtotal || payment.amount / 1.05) * discount.amount / 100)
                        : discount.amount).toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                  <span>Total Tax</span>
                  <span>${(Math.round((payment.booking_services?.reduce((sum, service) => sum + (service.tax_amount || 0), 0) || 0) * 100) / 100).toLocaleString()}</span>
                </div>
              </>
            ) : (
              // Simple breakdown when detailed service data is not available
              <>
                <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                  <span>Service Total</span>
                  <span>${(payment.amount / 1.05).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                  <span>Tax</span>
                  <span>${(payment.amount - (payment.amount / 1.05)).toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
              <span>Total Amount</span>
              <span>${payment.amount.toLocaleString()}</span>
            </div>
            {/* Show tip information if available */}
            {(payment as any).tip_amount && (payment as any).tip_amount > 0 && (
              <div className="flex justify-between text-xs sm:text-sm text-green-600 pt-1">
                <span>Tip Amount</span>
                <span>${(payment as any).tip_amount.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 